import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
    name: "",
  });

  useEffect(() => {
    // Check if logged in
    const token = localStorage.getItem("admin_token");
    if (token) {
      navigate("/admin/dashboard");
      return;
    }
    setCheckingSetup(false);
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!credentials.email || !credentials.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        email: credentials.email,
        password: credentials.password,
      });
      localStorage.setItem("admin_token", res.data.token);
      localStorage.setItem("admin_email", res.data.email);
      localStorage.setItem("admin_name", res.data.name);
      toast.success("Welcome back!");
      navigate("/admin/dashboard");
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Invalid credentials");
      } else {
        toast.error("Login failed. Try setup if this is your first time.");
        setIsSetup(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (!credentials.email || !credentials.password || !credentials.name) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/setup`, {
        email: credentials.email,
        password: credentials.password,
        name: credentials.name,
      });
      localStorage.setItem("admin_token", res.data.token);
      localStorage.setItem("admin_email", res.data.email);
      localStorage.setItem("admin_name", res.data.name);
      toast.success("Admin account created!");
      navigate("/admin/dashboard");
    } catch (e) {
      if (e.response?.data?.detail === "Admin already exists") {
        toast.error("Admin already exists. Please login.");
        setIsSetup(false);
      } else {
        const errorDetail = e.response?.data?.detail;
        const errorMsg = typeof errorDetail === 'string' ? errorDetail : 
                         Array.isArray(errorDetail) ? errorDetail[0]?.msg || "Setup failed" :
                         "Setup failed";
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#134E4A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#134E4A] text-white mx-auto mb-6 flex items-center justify-center">
            <Lock size={24} />
          </div>
          <h1 className="font-serif text-3xl text-[#1C1917] mb-2">
            {isSetup ? "Admin Setup" : "Admin Login"}
          </h1>
          <p className="text-[#78716C]">
            {isSetup
              ? "Create your admin account"
              : "Sign in to manage your property"}
          </p>
        </div>

        <div className="bg-white border border-stone-200 p-8">
          <form onSubmit={isSetup ? handleSetup : handleLogin}>
            <div className="space-y-6">
              {isSetup && (
                <div>
                  <Label className="text-sm uppercase tracking-widest text-[#78716C] mb-2 block">
                    Full Name
                  </Label>
                  <Input
                    data-testid="admin-name"
                    value={credentials.name}
                    onChange={(e) =>
                      setCredentials({ ...credentials, name: e.target.value })
                    }
                    placeholder="Your name"
                    className="border-0 border-b border-stone-300 rounded-none px-0 py-4 focus:ring-0 focus:border-[#134E4A]"
                  />
                </div>
              )}

              <div>
                <Label className="text-sm uppercase tracking-widest text-[#78716C] mb-2 block">
                  Email Address
                </Label>
                <Input
                  data-testid="admin-email"
                  type="email"
                  value={credentials.email}
                  onChange={(e) =>
                    setCredentials({ ...credentials, email: e.target.value })
                  }
                  placeholder="admin@example.com"
                  className="border-0 border-b border-stone-300 rounded-none px-0 py-4 focus:ring-0 focus:border-[#134E4A]"
                />
              </div>

              <div>
                <Label className="text-sm uppercase tracking-widest text-[#78716C] mb-2 block">
                  Password
                </Label>
                <Input
                  data-testid="admin-password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) =>
                    setCredentials({ ...credentials, password: e.target.value })
                  }
                  placeholder="••••••••"
                  className="border-0 border-b border-stone-300 rounded-none px-0 py-4 focus:ring-0 focus:border-[#134E4A]"
                />
              </div>

              <Button
                data-testid="admin-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#134E4A] hover:bg-[#0f3d3a] text-white py-6 text-sm uppercase tracking-widest mt-4"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isSetup ? "Creating..." : "Signing in..."}
                  </>
                ) : isSetup ? (
                  "Create Account"
                ) : (
                  "Sign In"
                )}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsSetup(!isSetup)}
              className="text-[#134E4A] text-sm hover:underline"
            >
              {isSetup ? "Already have an account? Sign in" : "First time? Create admin account"}
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-[#78716C] text-sm hover:text-[#1C1917] transition-colors"
          >
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
}
